/**
 * ClienteEditPage Component
 * 
 * Página dedicada para edição de clientes.
 * Substitui o modal anterior por uma página completa seguindo o estilo do PermissionConfigPage.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, User, Save, Loader2, Gift, ShoppingBag, CreditCard, AlertCircle, Plus, Minus, X } from 'lucide-react'
import FormField from '../../../../components/ERP/Form/FormField'
import { customerService, customerCreditService, saleService, accountReceivableService } from '../../../../services/erpApi'
import { useToast } from '../../../../hooks/useToast'
import { usePermissions } from '../../../../hooks/usePermissions'
import { MODULES, ACTIONS } from '../../../../utils/permissions'
import { Toast } from '../../../../components'
import './ClienteEditPage.css'

interface BonusMovement {
  id: string
  movementType: 'credit' | 'debit'
  subtype: string
  date: string
  amount: number
  signedAmount: number
  description: string
  sale?: { id: number; number: string } | null
  returnId?: number | null
  createdByName?: string | null
}

function formatBonusCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

function getMovementSubtypeLabel(subtype: string, returnId?: number | null) {
  if (subtype === 'sale') return 'Venda'
  if (subtype === 'migration') return 'Migração'
  if (subtype === 'return' || returnId) return 'Devolução'
  if (subtype === 'manual') return 'Manual'
  return subtype
}

interface Cliente {
  id: number
  name: string
  email?: string
  phone?: string
  cpf_cnpj?: string
  address_street?: string
  address_number?: string
  address_complement?: string
  address_neighborhood?: string
  address_city?: string
  address_state?: string
  address_zipcode?: string
  address_country?: string
  type?: string
  status?: string
  state_registration?: string
  state_registration_exempt?: boolean
}

interface FormData {
  name: string
  email: string
  phone: string
  cpfCnpj: string
  addressStreet: string
  addressNumber: string
  addressComplement: string
  addressNeighborhood: string
  addressCity: string
  addressState: string
  addressZipcode: string
  addressCountry: string
  type: string
  status: string
  stateRegistration: string
  stateRegistrationExempt: boolean
}

const ClienteEditPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { toast, showToast, hideToast } = useToast()
  const { hasPermission } = usePermissions()
  const canAdjustBonus = hasPermission(MODULES.PEOPLE, ACTIONS.PEOPLE_CUSTOMERS_BONUS_MANUAL)
  
  const [cliente, setCliente] = useState<Cliente | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    cpfCnpj: '',
    addressStreet: '',
    addressNumber: '',
    addressComplement: '',
    addressNeighborhood: '',
    addressCity: '',
    addressState: '',
    addressZipcode: '',
    addressCountry: 'BR',
    type: 'F',
    status: 'active',
    stateRegistration: '',
    stateRegistrationExempt: false
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  
  // Tabs state
  const [activeTab, setActiveTab] = useState<'bonus' | 'compras' | 'pagamentos'>('bonus')
  
  // Bonus/Credits state
  const [bonusLoading, setBonusLoading] = useState(false)
  const [bonusMovements, setBonusMovements] = useState<BonusMovement[]>([])
  const [bonusBalance, setBonusBalance] = useState<any>(null)
  const [creditModalOpen, setCreditModalOpen] = useState(false)
  const [debitModalOpen, setDebitModalOpen] = useState(false)
  const [bonusSaving, setBonusSaving] = useState(false)
  const [creditForm, setCreditForm] = useState({
    amount: '',
    description: '',
    source: 'migration' as 'manual' | 'migration',
    expirationDate: ''
  })
  const [debitForm, setDebitForm] = useState({
    amount: '',
    description: ''
  })
  
  // Purchase history state
  const [comprasLoading, setComprasLoading] = useState(false)
  const [comprasData, setComprasData] = useState<any[]>([])
  
  // Payment history state
  const [pagamentosLoading, setPagamentosLoading] = useState(false)
  const [pagamentosData, setPagamentosData] = useState<any[]>([])
  const [overdueCount, setOverdueCount] = useState(0)

  useEffect(() => {
    const loadCliente = async () => {
      try {
        setLoading(true)
        setError(null)
        
        if (!id) {
          // Modo criação
          setLoading(false)
          return
        }
        
        const response = await customerService.getById(parseInt(id))
        
        if (response.success && response.data) {
          const clienteData = response.data
          setCliente(clienteData)
          
          setFormData({
            name: clienteData.name || '',
            email: clienteData.email || '',
            phone: clienteData.phone || '',
            cpfCnpj: clienteData.cpf_cnpj || '',
            addressStreet: clienteData.address_street || '',
            addressNumber: clienteData.address_number || '',
            addressComplement: clienteData.address_complement || '',
            addressNeighborhood: clienteData.address_neighborhood || '',
            addressCity: clienteData.address_city || '',
            addressState: clienteData.address_state || '',
            addressZipcode: clienteData.address_zipcode || '',
            addressCountry: clienteData.address_country || 'BR',
            type: clienteData.type || 'F',
            status: clienteData.status || 'active',
            stateRegistration: clienteData.state_registration || '',
            stateRegistrationExempt: clienteData.state_registration_exempt === true
          })
        } else {
          setError('Cliente não encontrado')
          showToast('Cliente não encontrado', 'error')
        }
      } catch (error: any) {
        console.error('Erro ao carregar cliente:', error)
        const errorMessage = error.response?.data?.message || 'Erro ao carregar cliente'
        setError(errorMessage)
        showToast(errorMessage, 'error')
      } finally {
        setLoading(false)
      }
    }

    loadCliente()
  }, [id, showToast])

  const loadBonus = useCallback(async () => {
    if (!id) return
    try {
      setBonusLoading(true)
      const response = await customerCreditService.getLedger(parseInt(id))
      if (response.success) {
        setBonusMovements(response.data?.movements || [])
        setBonusBalance(response.data?.balance || null)
      }
    } catch (error) {
      console.error('Erro ao carregar bônus:', error)
    } finally {
      setBonusLoading(false)
    }
  }, [id])

  const handleAddCredit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id) return

    const amount = parseFloat(creditForm.amount.replace(',', '.'))
    if (!amount || amount <= 0) {
      showToast('Informe um valor válido', 'error')
      return
    }
    if (creditForm.description.trim().length < 5) {
      showToast('Descrição é obrigatória (mínimo 5 caracteres)', 'error')
      return
    }

    setBonusSaving(true)
    try {
      const response = await customerCreditService.createManual({
        customerId: parseInt(id, 10),
        amount,
        description: creditForm.description.trim(),
        source: creditForm.source,
        expirationDate: creditForm.expirationDate || null
      })
      if (response.success) {
        showToast('Bônus adicionado com sucesso', 'success')
        setCreditModalOpen(false)
        setCreditForm({ amount: '', description: '', source: 'migration', expirationDate: '' })
        await loadBonus()
      } else {
        showToast(response.message || 'Erro ao adicionar bônus', 'error')
      }
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Erro ao adicionar bônus', 'error')
    } finally {
      setBonusSaving(false)
    }
  }

  const handleAddDebit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id) return

    const amount = parseFloat(debitForm.amount.replace(',', '.'))
    if (!amount || amount <= 0) {
      showToast('Informe um valor válido', 'error')
      return
    }
    if (debitForm.description.trim().length < 5) {
      showToast('Descrição é obrigatória (mínimo 5 caracteres)', 'error')
      return
    }
    const available = parseFloat(bonusBalance?.availableBalance || 0)
    if (amount > available + 0.009) {
      showToast(`Saldo insuficiente (disponível: ${formatBonusCurrency(available)})`, 'error')
      return
    }

    setBonusSaving(true)
    try {
      const response = await customerCreditService.createManualDebit({
        customerId: parseInt(id, 10),
        amount,
        description: debitForm.description.trim()
      })
      if (response.success) {
        showToast('Bônus reduzido com sucesso', 'success')
        setDebitModalOpen(false)
        setDebitForm({ amount: '', description: '' })
        await loadBonus()
      } else {
        showToast(response.message || 'Erro ao reduzir bônus', 'error')
      }
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Erro ao reduzir bônus', 'error')
    } finally {
      setBonusSaving(false)
    }
  }

  const loadCompras = useCallback(async () => {
    if (!id) return
    try {
      setComprasLoading(true)
      // Filtrar apenas vendas efetivadas (não pré-vendas) com status completed
      const response = await saleService.getAll({ 
        customerId: id, 
        status: 'completed', // Apenas vendas concluídas
        limit: 100 // Buscar mais para filtrar depois
      })
      if (response.success) {
        // Filtrar no frontend para excluir pré-vendas
        const vendasEfetivadas = (response.data || []).filter((sale: any) => 
          sale.type !== 'presale' && sale.status === 'completed'
        )
        setComprasData(vendasEfetivadas.slice(0, 50)) // Limitar a 50
      }
    } catch (error) {
      console.error('Erro ao carregar compras:', error)
    } finally {
      setComprasLoading(false)
    }
  }, [id])

  const loadPagamentos = useCallback(async () => {
    if (!id) return
    try {
      setPagamentosLoading(true)
      const response = await accountReceivableService.getAll({ customerId: id, limit: 100 })
      if (response.success) {
        const data = response.data || []
        setPagamentosData(data)
        
        // Contar títulos vencidos
        const today = new Date()
        const overdue = data.filter((item: any) => {
          if (item.status !== 'pending') return false
          const dueDate = new Date(item.due_date)
          return dueDate < today
        })
        setOverdueCount(overdue.length)
      }
    } catch (error) {
      console.error('Erro ao carregar pagamentos:', error)
    } finally {
      setPagamentosLoading(false)
    }
  }, [id])

  // Load tab data when tab changes and client is loaded
  useEffect(() => {
    if (!id || !cliente) return

    const loadTabData = async () => {
      switch (activeTab) {
        case 'bonus':
          await loadBonus()
          break
        case 'compras':
          await loadCompras()
          break
        case 'pagamentos':
          await loadPagamentos()
          break
      }
    }

    loadTabData()
  }, [activeTab, id, cliente, loadBonus, loadCompras, loadPagamentos])

  const handleSave = async () => {
    setErrors({})
    
    try {
      setSaving(true)
      
      // Preparar dados no formato esperado pelo backend
      const dataToSend = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        cpfCnpj: formData.cpfCnpj ? formData.cpfCnpj.replace(/\D/g, '') : null,
        addressStreet: formData.addressStreet || null,
        addressNumber: formData.addressNumber || null,
        addressComplement: formData.addressComplement || null,
        addressNeighborhood: formData.addressNeighborhood || null,
        addressCity: formData.addressCity || null,
        addressState: formData.addressState || null,
        addressZipcode: formData.addressZipcode ? formData.addressZipcode.replace(/\D/g, '') : null,
        addressCountry: formData.addressCountry || 'BR',
        type: formData.type || 'F',
        status: formData.status || 'active',
        stateRegistration: formData.stateRegistrationExempt
          ? null
          : (formData.stateRegistration ? formData.stateRegistration.replace(/\D/g, '') : null),
        stateRegistrationExempt: formData.type === 'J' ? formData.stateRegistrationExempt : false
      }

      let response
      if (id) {
        response = await customerService.update(parseInt(id), dataToSend)
      } else {
        response = await customerService.create(dataToSend)
      }

      if (response.success) {
        showToast(id ? 'Cliente atualizado com sucesso!' : 'Cliente criado com sucesso!', 'success')
        
        // Navegar de volta após um pequeno delay
        setTimeout(() => {
          navigate('/erp/pessoas/clientes')
        }, 1000)
      } else {
        // Tratar erros de validação
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj: Record<string, string> = {}
          response.errors.forEach((err: any) => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          const errorMessage = response.message || (id ? 'Erro ao atualizar cliente' : 'Erro ao criar cliente')
          showToast(errorMessage, 'error')
          setErrors({ submit: errorMessage })
        }
      }
    } catch (error: any) {
      console.error('Erro ao salvar cliente:', error)
      const errorMessage = error.response?.data?.message || (id ? 'Erro ao atualizar cliente' : 'Erro ao criar cliente')
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj: Record<string, string> = {}
        errorDetails.forEach((err: any) => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
      showToast(errorMessage, 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    navigate('/erp/pessoas/clientes')
  }

  if (loading) {
    return (
      <div className="cliente-edit-page">
        <div className="cliente-edit-loading">
          <Loader2 size={32} className="cliente-edit-spinner" />
          <p>Carregando cliente...</p>
        </div>
      </div>
    )
  }

  if (error && id) {
    return (
      <div className="cliente-edit-page">
        <div className="cliente-edit-error">
          <p>{error}</p>
          <button 
            className="cliente-edit-btn-secondary"
            onClick={() => navigate('/erp/pessoas/clientes')}
          >
            <ArrowLeft size={16} />
            Voltar para Clientes
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="cliente-edit-page">
      {/* Header */}
      <div className="cliente-edit-header">
        <div>
          <h1 className="cliente-edit-title">
            {id ? 'Editar' : 'Novo'} <span className="cliente-edit-title-accent">Cliente</span>
          </h1>
          <p className="cliente-edit-subtitle">
            {id ? `Editando informações do cliente` : 'Preencha os dados para criar um novo cliente'}
          </p>
        </div>
        <div className="cliente-edit-actions">
          <button 
            className="cliente-edit-btn-secondary"
            onClick={handleCancel}
            disabled={saving}
          >
            <ArrowLeft size={16} />
            Cancelar
          </button>
          <button 
            className="cliente-edit-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 size={16} className="cliente-edit-spinner" />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} />
                {id ? 'Salvar Alterações' : 'Criar Cliente'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Info Card */}
      {id && cliente && (
        <div className="cliente-edit-info-card">
          <div className="cliente-edit-info-header">
            <User size={20} />
            <h3>Informações do Cliente</h3>
          </div>
          <div className="cliente-edit-info-content">
            <div className="cliente-edit-info-item">
              <span className="cliente-edit-info-label">ID</span>
              <span className="cliente-edit-info-value">#{cliente.id}</span>
            </div>
            <div className="cliente-edit-info-item">
              <span className="cliente-edit-info-label">Status</span>
              <span className={`cliente-edit-status-badge ${cliente.status === 'active' ? 'active' : cliente.status === 'blocked' ? 'blocked' : 'inactive'}`}>
                {cliente.status === 'active' ? 'Ativo' : cliente.status === 'blocked' ? 'Bloqueado' : 'Inativo'}
              </span>
            </div>
            <div className="cliente-edit-info-item">
              <span className="cliente-edit-info-label">Tipo</span>
              <span className="cliente-edit-info-value">
                {cliente.type === 'F' ? 'Pessoa Física' : 'Pessoa Jurídica'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Form Card - Dados Básicos */}
      <div className="cliente-edit-card">
        <div className="cliente-edit-card-header">
          <h3 className="cliente-edit-card-title">Dados Básicos</h3>
          <p className="cliente-edit-card-description">
            Informações principais do cliente
          </p>
        </div>
        <div className="cliente-edit-card-content">
          <FormField
            label={formData.type === 'J' ? 'Razão social' : 'Nome'}
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            placeholder={formData.type === 'J' ? 'Razão social' : 'Nome completo'}
            mask=""
            required
          />

          <div className="cliente-edit-form-row">
            <FormField
              label="E-mail"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={errors.email}
              placeholder="email@exemplo.com"
              mask=""
            />

            <FormField
              label="Telefone"
              name="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              error={errors.phone}
              placeholder="(00) 00000-0000"
              mask="phone"
            />
          </div>

          <div className="cliente-edit-form-row">
            <FormField
              label="CPF/CNPJ"
              name="cpfCnpj"
              value={formData.cpfCnpj}
              onChange={(e) => {
                const value = e.target.value
                // Aplicar máscara baseada no tamanho
                const cleanValue = value.replace(/\D/g, '')
                const masked = cleanValue.length <= 11
                  ? cleanValue.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2')
                  : cleanValue.replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d)/, '$1-$2')
                const nextType = cleanValue.length <= 11 ? 'F' : 'J'
                setFormData({
                  ...formData,
                  cpfCnpj: masked,
                  type: nextType,
                  stateRegistrationExempt: nextType === 'J' ? (formData.stateRegistrationExempt || !formData.stateRegistration) : false
                })
              }}
              error={errors.cpfCnpj}
              placeholder="000.000.000-00 ou 00.000.000/0000-00"
              mask=""
            />

            <FormField
              label="Tipo"
              name="type"
              type="select"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              error=""
              placeholder=""
              mask=""
              options={[
                { value: 'F', label: 'Pessoa Física' },
                { value: 'J', label: 'Pessoa Jurídica' }
              ]}
            />

            <FormField
              label="Status"
              name="status"
              type="select"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              error=""
              placeholder=""
              mask=""
              options={[
                { value: 'active', label: 'Ativo' },
                { value: 'inactive', label: 'Inativo' },
                { value: 'blocked', label: 'Bloqueado' }
              ]}
            />
          </div>

          {formData.type === 'J' && (
            <div className="cliente-edit-fiscal-section">
              <p className="cliente-edit-card-description" style={{ marginBottom: '0.75rem' }}>
                Dados fiscais para emissão de NF-e (modelo 55)
              </p>
              <label className="cliente-edit-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.stateRegistrationExempt}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      stateRegistrationExempt: e.target.checked,
                      stateRegistration: e.target.checked ? '' : formData.stateRegistration
                    })
                  }
                />
                Isento de Inscrição Estadual
              </label>
              {!formData.stateRegistrationExempt && (
                <FormField
                  label="Inscrição Estadual (IE)"
                  name="stateRegistration"
                  value={formData.stateRegistration}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      stateRegistration: e.target.value.replace(/\D/g, '')
                    })
                  }
                  error={errors.stateRegistration}
                  placeholder="Somente números"
                  mask=""
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Form Card - Endereço */}
      <div className="cliente-edit-card">
        <div className="cliente-edit-card-header">
          <h3 className="cliente-edit-card-title">Endereço</h3>
          <p className="cliente-edit-card-description">
            Informações de localização do cliente
          </p>
        </div>
        <div className="cliente-edit-card-content">
          <FormField
            label="Rua"
            name="addressStreet"
            value={formData.addressStreet}
            onChange={(e) => setFormData({ ...formData, addressStreet: e.target.value })}
            error={errors.addressStreet}
            placeholder="Nome da rua, avenida, etc."
            mask=""
          />

          <div className="cliente-edit-form-row">
            <FormField
              label="Número"
              name="addressNumber"
              value={formData.addressNumber}
              onChange={(e) => setFormData({ ...formData, addressNumber: e.target.value })}
              error={errors.addressNumber}
              placeholder="Número"
              mask=""
            />

            <FormField
              label="Complemento"
              name="addressComplement"
              value={formData.addressComplement}
              onChange={(e) => setFormData({ ...formData, addressComplement: e.target.value })}
              error={errors.addressComplement}
              placeholder="Apto, Bloco, etc."
              mask=""
            />
          </div>

          <FormField
            label="Bairro"
            name="addressNeighborhood"
            value={formData.addressNeighborhood}
            onChange={(e) => setFormData({ ...formData, addressNeighborhood: e.target.value })}
            error={errors.addressNeighborhood}
            placeholder="Nome do bairro"
            mask=""
          />

          <div className="cliente-edit-form-row">
            <FormField
              label="Cidade"
              name="addressCity"
              value={formData.addressCity}
              onChange={(e) => setFormData({ ...formData, addressCity: e.target.value })}
              error={errors.addressCity}
              placeholder="Nome da cidade"
              mask=""
            />

            <FormField
              label="Estado"
              name="addressState"
              value={formData.addressState}
              onChange={(e) => setFormData({ ...formData, addressState: e.target.value.toUpperCase() })}
              error={errors.addressState}
              placeholder="UF"
              mask=""
              maxLength={2}
            />

            <FormField
              label="CEP"
              name="addressZipcode"
              value={formData.addressZipcode}
              onChange={(e) => setFormData({ ...formData, addressZipcode: e.target.value })}
              error={errors.addressZipcode}
              placeholder="00000-000"
              mask="cep"
            />
          </div>

          <FormField
            label="País"
            name="addressCountry"
            value={formData.addressCountry}
            onChange={(e) => setFormData({ ...formData, addressCountry: e.target.value })}
            error={errors.addressCountry}
            placeholder="País"
            mask=""
          />
        </div>
      </div>

      {errors.submit && (
        <div className="cliente-edit-error-message">{errors.submit}</div>
      )}

      {/* Tabs Section - Only show if editing existing client */}
      {id && cliente && (
        <div className="cliente-edit-tabs-section">
          <div className="cliente-edit-tabs-header">
            <button 
              onClick={() => setActiveTab('bonus')}
              className={`cliente-edit-tab-button ${activeTab === 'bonus' ? 'active' : ''}`}
            >
              <Gift size={18} />
              Bônus
              {activeTab === 'bonus' && <div className="cliente-edit-tab-indicator" />}
            </button>
            <button 
              onClick={() => setActiveTab('compras')}
              className={`cliente-edit-tab-button ${activeTab === 'compras' ? 'active' : ''}`}
            >
              <ShoppingBag size={18} />
              Histórico de Compras
              {activeTab === 'compras' && <div className="cliente-edit-tab-indicator" />}
            </button>
            <button 
              onClick={() => setActiveTab('pagamentos')}
              className={`cliente-edit-tab-button ${activeTab === 'pagamentos' ? 'active' : ''}`}
            >
              <CreditCard size={18} />
              Histórico de Pagamentos
              {overdueCount > 0 && (
                <span className="cliente-edit-tab-badge">{overdueCount}</span>
              )}
              {activeTab === 'pagamentos' && <div className="cliente-edit-tab-indicator" />}
            </button>
          </div>

          <div className="cliente-edit-tabs-content">
            {activeTab === 'bonus' && (
              <div className="cliente-edit-tab-content-inner">
                {bonusLoading ? (
                  <div className="cliente-edit-tab-loading">
                    <Loader2 size={24} className="cliente-edit-spinner" />
                    <p>Carregando bônus...</p>
                  </div>
                ) : (
                  <>
                    {bonusBalance && (
                      <div className="cliente-edit-bonus-summary">
                        <div className="cliente-edit-bonus-summary-item">
                          <span className="cliente-edit-bonus-summary-label">Saldo Disponível</span>
                          <span className="cliente-edit-bonus-summary-value">
                            R$ {bonusBalance.availableBalance?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                        <div className="cliente-edit-bonus-summary-item">
                          <span className="cliente-edit-bonus-summary-label">Total de Créditos</span>
                          <span className="cliente-edit-bonus-summary-value">
                            R$ {bonusBalance.totalCredits?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                        <div className="cliente-edit-bonus-summary-item">
                          <span className="cliente-edit-bonus-summary-label">Total Utilizado</span>
                          <span className="cliente-edit-bonus-summary-value">
                            R$ {bonusBalance.totalUsed?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="cliente-edit-bonus-list">
                      <div className="cliente-edit-bonus-list-header">
                        <h4 className="cliente-edit-tab-section-title">Extrato de movimentações</h4>
                        {canAdjustBonus && (
                          <div className="cliente-edit-bonus-actions">
                            <button
                              type="button"
                              className="cliente-edit-bonus-btn cliente-edit-bonus-btn--credit"
                              title="Adicionar bônus"
                              onClick={() => setCreditModalOpen(true)}
                            >
                              <Plus size={18} />
                            </button>
                            <button
                              type="button"
                              className="cliente-edit-bonus-btn cliente-edit-bonus-btn--debit"
                              title="Diminuir bônus"
                              onClick={() => setDebitModalOpen(true)}
                              disabled={(bonusBalance?.availableBalance || 0) <= 0}
                            >
                              <Minus size={18} />
                            </button>
                          </div>
                        )}
                      </div>
                      {bonusMovements.length === 0 ? (
                        <p className="cliente-edit-empty-message">Nenhuma movimentação de bônus registrada</p>
                      ) : (
                        <div className="cliente-edit-table-wrapper">
                          <table className="cliente-edit-table cliente-edit-bonus-ledger-table">
                            <thead>
                              <tr>
                                <th>Data</th>
                                <th>Tipo</th>
                                <th>Descrição</th>
                                <th>Venda</th>
                                <th className="cliente-edit-th-right">Valor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bonusMovements.map((movement) => (
                                <tr key={movement.id}>
                                  <td>{new Date(movement.date).toLocaleDateString('pt-BR')}</td>
                                  <td>
                                    <span className={`cliente-edit-bonus-type-badge cliente-edit-bonus-type-badge--${movement.movementType}`}>
                                      {movement.movementType === 'credit' ? 'Crédito' : 'Débito'}
                                    </span>
                                    <span className="cliente-edit-bonus-subtype">
                                      {getMovementSubtypeLabel(movement.subtype, movement.returnId)}
                                    </span>
                                  </td>
                                  <td>{movement.description}</td>
                                  <td>
                                    {movement.sale ? (
                                      <button
                                        type="button"
                                        className="cliente-edit-bonus-sale-link"
                                        onClick={() => navigate('/erp/vendas/relacao-vendas')}
                                      >
                                        #{movement.sale.number}
                                      </button>
                                    ) : movement.returnId ? (
                                      <button
                                        type="button"
                                        className="cliente-edit-bonus-sale-link"
                                        onClick={() => navigate(`/erp/estoque/trocas/${movement.returnId}/editar`)}
                                      >
                                        Troca #{movement.returnId}
                                      </button>
                                    ) : (
                                      '—'
                                    )}
                                  </td>
                                  <td className={`cliente-edit-td-right cliente-edit-bonus-amount cliente-edit-bonus-amount--${movement.movementType}`}>
                                    {movement.movementType === 'credit' ? '+' : '-'}
                                    {formatBonusCurrency(Math.abs(movement.amount))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'compras' && (
              <div className="cliente-edit-tab-content-inner">
                {comprasLoading ? (
                  <div className="cliente-edit-tab-loading">
                    <Loader2 size={24} className="cliente-edit-spinner" />
                    <p>Carregando histórico de compras...</p>
                  </div>
                ) : (
                  <>
                    <div className="cliente-edit-compras-list">
                      <h4 className="cliente-edit-tab-section-title">Últimas Compras</h4>
                      {comprasData.length === 0 ? (
                        <p className="cliente-edit-empty-message">Nenhuma compra encontrada</p>
                      ) : (
                        <div className="cliente-edit-table-wrapper">
                          <table className="cliente-edit-table">
                            <thead>
                              <tr>
                                <th>Data</th>
                                <th>Número</th>
                                <th>Valor Total</th>
                                <th>Status</th>
                                <th>Tipo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {comprasData.map((sale: any) => (
                                <tr key={sale.id}>
                                  <td>{new Date(sale.sale_date || sale.created_at).toLocaleDateString('pt-BR')}</td>
                                  <td>{sale.number || `#${sale.id}`}</td>
                                  <td>R$ {parseFloat(sale.final_amount || sale.total_amount || 0).toFixed(2)}</td>
                                  <td>
                                    <span className={`cliente-edit-status-badge ${sale.status}`}>
                                      {sale.status === 'completed' ? 'Concluída' : 
                                       sale.status === 'pending' ? 'Pendente' : 
                                       sale.status === 'cancelled' ? 'Cancelada' : sale.status}
                                    </span>
                                  </td>
                                  <td>Venda</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'pagamentos' && (
              <div className="cliente-edit-tab-content-inner">
                {pagamentosLoading ? (
                  <div className="cliente-edit-tab-loading">
                    <Loader2 size={24} className="cliente-edit-spinner" />
                    <p>Carregando histórico de pagamentos...</p>
                  </div>
                ) : (
                  <>
                    {overdueCount > 0 && (
                      <div className="cliente-edit-overdue-alert">
                        <AlertCircle size={20} />
                        <div>
                          <strong>Atenção!</strong>
                          <p>Este cliente possui {overdueCount} título(s) em atraso.</p>
                        </div>
                      </div>
                    )}
                    <div className="cliente-edit-pagamentos-list">
                      <h4 className="cliente-edit-tab-section-title">Títulos a Receber</h4>
                      {pagamentosData.length === 0 ? (
                        <p className="cliente-edit-empty-message">Nenhum título encontrado</p>
                      ) : (
                        <div className="cliente-edit-table-wrapper">
                          <table className="cliente-edit-table">
                            <thead>
                              <tr>
                                <th>Vencimento</th>
                                <th>Valor</th>
                                <th>Pago</th>
                                <th>Pendente</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pagamentosData.map((item: any) => {
                                const dueDate = new Date(item.due_date)
                                const today = new Date()
                                const isOverdue = dueDate < today && item.status === 'pending'
                                const pendingAmount = parseFloat(item.amount || 0) - parseFloat(item.paid_amount || 0)
                                
                                return (
                                  <tr key={item.id} className={isOverdue ? 'cliente-edit-row-overdue' : ''}>
                                    <td>{dueDate.toLocaleDateString('pt-BR')}</td>
                                    <td>R$ {parseFloat(item.amount || 0).toFixed(2)}</td>
                                    <td>R$ {parseFloat(item.paid_amount || 0).toFixed(2)}</td>
                                    <td>R$ {pendingAmount.toFixed(2)}</td>
                                    <td>
                                      <span className={`cliente-edit-status-badge ${item.status} ${isOverdue ? 'overdue' : ''}`}>
                                        {isOverdue ? 'Vencido' : 
                                         item.status === 'paid' ? 'Pago' : 
                                         item.status === 'pending' ? 'Pendente' : item.status}
                                      </span>
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {creditModalOpen && (
        <div className="cliente-edit-bonus-modal-overlay" onClick={() => !bonusSaving && setCreditModalOpen(false)}>
          <div className="cliente-edit-bonus-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cliente-edit-bonus-modal-header">
              <h3>Adicionar bônus</h3>
              <button type="button" onClick={() => setCreditModalOpen(false)} disabled={bonusSaving}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddCredit} className="cliente-edit-bonus-modal-form">
              <label className="cliente-edit-bonus-modal-label">
                Valor (R$)
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  value={creditForm.amount}
                  onChange={(e) => setCreditForm((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder="0,00"
                />
              </label>
              <label className="cliente-edit-bonus-modal-label">
                Origem
                <select
                  value={creditForm.source}
                  onChange={(e) =>
                    setCreditForm((prev) => ({
                      ...prev,
                      source: e.target.value as 'manual' | 'migration'
                    }))
                  }
                >
                  <option value="migration">Migração sistema antigo</option>
                  <option value="manual">Ajuste manual</option>
                </select>
              </label>
              <label className="cliente-edit-bonus-modal-label">
                Descrição / motivo
                <textarea
                  rows={3}
                  required
                  minLength={5}
                  value={creditForm.description}
                  onChange={(e) => setCreditForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Ex.: Migração WF legado — saldo em 01/07/2026"
                />
              </label>
              <label className="cliente-edit-bonus-modal-label">
                Validade (opcional)
                <input
                  type="date"
                  value={creditForm.expirationDate}
                  onChange={(e) => setCreditForm((prev) => ({ ...prev, expirationDate: e.target.value }))}
                />
              </label>
              <div className="cliente-edit-bonus-modal-actions">
                <button type="button" onClick={() => setCreditModalOpen(false)} disabled={bonusSaving}>
                  Cancelar
                </button>
                <button type="submit" className="cliente-edit-bonus-modal-submit" disabled={bonusSaving}>
                  {bonusSaving ? <Loader2 size={16} className="cliente-edit-spinner" /> : 'Confirmar crédito'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {debitModalOpen && (
        <div className="cliente-edit-bonus-modal-overlay" onClick={() => !bonusSaving && setDebitModalOpen(false)}>
          <div className="cliente-edit-bonus-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cliente-edit-bonus-modal-header">
              <h3>Diminuir bônus</h3>
              <button type="button" onClick={() => setDebitModalOpen(false)} disabled={bonusSaving}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddDebit} className="cliente-edit-bonus-modal-form">
              <p className="cliente-edit-bonus-modal-hint">
                Saldo disponível: {formatBonusCurrency(parseFloat(bonusBalance?.availableBalance || 0))}.
                O valor será descontado do saldo (FIFO nos lotes de crédito).
              </p>
              <label className="cliente-edit-bonus-modal-label">
                Valor (R$)
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={bonusBalance?.availableBalance || undefined}
                  required
                  value={debitForm.amount}
                  onChange={(e) => setDebitForm((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder="0,00"
                />
              </label>
              <label className="cliente-edit-bonus-modal-label">
                Descrição / motivo
                <textarea
                  rows={3}
                  required
                  minLength={5}
                  value={debitForm.description}
                  onChange={(e) => setDebitForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Ex.: Correção de saldo migrado incorretamente"
                />
              </label>
              <div className="cliente-edit-bonus-modal-actions">
                <button type="button" onClick={() => setDebitModalOpen(false)} disabled={bonusSaving}>
                  Cancelar
                </button>
                <button type="submit" className="cliente-edit-bonus-modal-submit cliente-edit-bonus-modal-submit--debit" disabled={bonusSaving}>
                  {bonusSaving ? <Loader2 size={16} className="cliente-edit-spinner" /> : 'Confirmar débito'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  )
}

export default ClienteEditPage
